import { type CSSProperties, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import { MainText } from '@/components/Typography/NamedTypo';
import Label from '@/features/Feedback/field/Tag/Label';
import TagSelectedModal from '@/features/Feedback/field/Tag/TagSelectedModal';
import useCellEllipsisAsCount from '@/hooks/domain/useCellEllipsisAsCount';
import useFixed from '@/hooks/pure/useFixed';
import useTagStore from '@/stores/TagStore';

const LABEL_TEXT_MAX_WIDTH = 100;

interface Props {
  tagList?: string[];
  listStyle?: CSSProperties;
  onClick?: () => void;
  columnWidth?: number;
}

const TagSelectorOnRow = (props: Props) => {
  const { tagList, listStyle, onClick } = props;
  const { tagMap } = useTagStore((state) => ({
    tagMap: state.tagMap,
  }));

  const [tagModalStatus, setTagModalStatus] = useState<{
    status: boolean;
    categoryKey?: string;
  } | null>(null);

  const ref = useRef(null);
  const pos = useFixed(ref, {
    offset: {
      y: 0,
    },
  });

  const selectedTags = useMemo(() => {
    return tagList?.filter((key) => tagMap[key]) || [];
  }, [tagList, tagMap]);

  const { ref: labelsWrapperRef, numTagsToShow } = useCellEllipsisAsCount({
    length: props.tagList?.length || 0,
    columnWidth: props.columnWidth,
  });

  return (
    <Style
      j="space-between"
      style={{ width: '100%', marginLeft: 3, position: 'relative' }}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onMouseOver={() => {
        setTagModalStatus({ status: true });
      }}
      onMouseLeave={() => {
        setTagModalStatus(null);
      }}
    >
      <Flex
        ref={labelsWrapperRef}
        a="center"
        g="4px"
        style={{
          // width: '100%',
          height: 44,
          flexWrap: 'nowrap',
          maxWidth: '400px',
          ...listStyle,
        }}
      >
        {selectedTags.slice(0, numTagsToShow).map((key: string) => {
          const tag = tagMap[key];
          const text = tag.level === 1 ? tag.name : `/${tag.name}`;
          return (
            <Label
              key={tag.alias}
              themeColor={tag.color}
              text={text}
              isSelected={false}
              withoutBorder
              maxWidth={LABEL_TEXT_MAX_WIDTH}
              style={{
                padding: '0 6px',
              }}
            />
          );
        })}
        {selectedTags && selectedTags.length > numTagsToShow && (
          <MainText dim>+{selectedTags.length - numTagsToShow}</MainText>
        )}
      </Flex>
      {tagModalStatus && selectedTags.length > 0 && (
        <TagSelectedModal
          {...props}
          selectedTags={selectedTags}
          closeModal={() => setTagModalStatus(null)}
          style={{ ...pos }}
        />
      )}
    </Style>
  );
};

const Style = styled(VFlex)`
  height: 40px;
  border: 1px solid ${(props) => props.theme.colors.ND00};
  border-radius: 4px;
  transition: border 0.1s;
  padding-right: 0 12px;
  cursor: default;
  .btn-add-tag {
    position: absolute;
    right: 4px;
    transition: all 0.2s;
    opacity: 0;
  }
  &:hover {
    border-color: ${(props) => props.theme.colors.gray500};
    .btn-add-tag {
      opacity: 1;
    }
  }
`;

export default TagSelectorOnRow;
